/**
 * Practice scenarios root catalog.
 *
 * Block 1 (33 PR-XX manifests · S02-S05) lives inline in this file
 *   Source: COORDINATION/app_rework_2026-04-29/15-block1-practice-tasks.md (ATELIER_TASKS Phase 4 done · 2026-05-01)
 *   Block 1 mountStrategy = 'inline-stub' for all (real iframe-mounted Atelier shell is Phase 2+ work)
 *   Block 1 Investigate-mode scenarios surface as 'review-only' fallback (manifest renders brief + taskMd
 *   + hints + scoring; per-scenario `customModule` JSX wiring follows in Block 2 sprint waves).
 *   `DEMO-CART` is a non-spec demo scenario kept for InlineCartSandbox + bug-engine smoke tests.
 *
 * Block 2+ (B{N}-{DOMAIN}-NN) lives in per-block subfolders per Stage A D4 split (ATELIER_TASKS_v2 W2.1+):
 *   ./block-2/index.ts → BLOCK_2_SCENARIOS (S07-S12 · API/SQL/WEB)
 *   ./block-3/index.ts → BLOCK_3_SCENARIOS (S13-S16 · SEL/POM/CI · ATELIER_TASKS_v2 W3.2)
 *   ./block-4/...      → forthcoming Wave 4
 *
 * Root export `SCENARIOS` concatenates Block 1 inline + per-block imports.
 */

import type { CalcErrorBug, Scenario } from '@/types';
import { BLOCK_2_SCENARIOS } from './block-2';
import { BLOCK_3_SCENARIOS } from './block-3';
import { BLOCK_4_SCENARIOS } from './block-4';
// Wave 5 · Block 1 Atelier integration · B1-FOUND-NN scenarios (separate from legacy
// PR-NN inline · PR-NN остаются в BLOCK_1_SCENARIOS const ниже · foreman territory frozen)
import { BLOCK_1_FOUND_SCENARIOS } from './block-1';

const DEMO_CART_BUG: CalcErrorBug = {
  id: 'B1',
  kind: 'calc-error',
  element: '[data-cart-total]',
  expected: 7128,
  actual: 7570,
  tolerance: 0.5,
  locale: 'ru-RU',
  authorNote: 'Промокод STUDIO10 (−10%) применился дважды в коде reducer-а',
};

const DEMO_CART_SCENARIO: Scenario = {
  id: 'DEMO-CART',
  sprint: 0,
  tier: 'required',
  mode: 'investigate',
  startRoute: '/sandbox/demo-cart',
  mountStrategy: 'inline-stub',
  title: 'В корзине что-то не сходится',
  brief: 'Юзер жалуется: «итог странный». Найди элемент с багом — кликни по нему.',
  taskMd:
    'Открой корзину. Внутри 3 товара + промокод. Подытог · промо · итого. Пройди расчёт глазами и кликни на элемент, где видишь ошибку.',
  bugs: [DEMO_CART_BUG],
  hints: [
    { text: 'Считай руками. Не верь итогу.' },
    { text: 'Подытог верный. Что-то с промо.' },
    { text: 'Скидка 10% — это 792 ₽, а из подытога вычли 1142 ₽.' },
  ],
  scoring: { baseScore: 5, firstAttemptBonus: 0, hintPenalty: 1, maxAttempts: 3 },
  achievementTriggers: ['detective-junior-tick'],
  estimateMin: 15,
  skills: { 'Test Design': 3, 'Practice Lab': 5 },
};

const BLOCK_1_SCENARIOS: ReadonlyArray<Scenario> = [
  DEMO_CART_SCENARIO,
  {
    id: 'PR-01',
    sprint: 2,
    tier: 'bonus',
    mode: 'investigate',
    startRoute: '/sandbox/pr-01',
    mountStrategy: 'inline-stub',
    title: "Sign Up без password length validation",
    brief: "PRD требует password ≥ 8 chars. UI принимает 2-символьный. Найди элемент.",
    taskMd: "Открой Sign Up. PRD §3.2: «Password must be ≥ 8 characters». Введи разные пароли (1-7 символов и 8+). Найди UI-элемент, который нарушает требование.",
    hints: [
      { text: "Введи короткий пароль (1-3 символа). Система должна возразить — а возражает?" },
      { text: "PRD требует минимум 8 символов. Какое поле должно ловить нарушение, но не ловит?" },
      { text: "Password field. Click на него — это hotspot. Validator отсутствует на client-side." },
    ],
    scoring: { baseScore: 5, firstAttemptBonus: 0, hintPenalty: 1, maxAttempts: 3 },
    achievementTriggers: ["detective-junior-tick", "sprint-02-bonus-tick", "curious-cat-tick"],
    estimateMin: 12,
    skills: { "Requirements Analysis": 3, "Practice Lab": 5, "QA Concepts": 2 },
  },
  {
    id: 'PR-02',
    sprint: 2,
    tier: 'bonus',
    mode: 'investigate',
    startRoute: '/sandbox/pr-02',
    mountStrategy: 'inline-stub',
    title: "Price formatting · wrong locale в RU context",
    brief: "PRD §4.7 требует RU formatting `1 234,50 ₽`. UI показывает `$1,234.50`. Найди элемент.",
    taskMd: "Открой портфолио художника Леры в RU locale. PRD §4.7: «Prices в RU locale = `1 234,50 ₽`». Просмотри карточки работ. Найди ту, где price formatting нарушает требование.",
    hints: [
      { text: "Сравни цены разных работ. Что-то отличается в формате?" },
      { text: "PRD требует РУБЛИ с пробелом в тысячах и запятой в копейках. Найди карточку с долларовым форматом." },
      { text: "Карточка `lera-2026-014` (третья по счёту). Price element показывает `$1,234.50` вместо `1 234,50 ₽`." },
    ],
    scoring: { baseScore: 5, firstAttemptBonus: 0, hintPenalty: 1, maxAttempts: 3 },
    achievementTriggers: ["detective-junior-tick", "sprint-02-bonus-tick", "curious-cat-tick"],
    estimateMin: 10,
    skills: { "Requirements Analysis": 2, "Practice Lab": 5, "Non-functional Testing": 1 },
  },
  {
    id: 'PR-03',
    sprint: 2,
    tier: 'required',
    mode: 'investigate',
    startRoute: '/sandbox/pr-03',
    mountStrategy: 'inline-stub',
    title: "Classify 6 requirement statements",
    brief: "PRD §3 содержит 6 statements. Классифицируй каждое по 4 типам.",
    taskMd: "Открой PRD §3. Прочитай 6 statements. Drag каждый на правильный bucket: Business · Functional · Non-functional · UI/UX. Цель: 6/6 correct = full score.",
    hints: [
      { text: "Business = goal/metric (что хотим достичь). Functional = behavior (что система делает). Non-functional = quality attribute (как · perf/security/load). UI/UX = visual/layout детали." },
      { text: "Statement про «20% sales lift» = goal → Business. Про «under 1.5 sec» = perf → Non-functional." },
      { text: "Финальная мапа: 1=Business · 2=Functional · 3=Non-functional · 4=UI · 5=Non-functional · 6=Functional." },
    ],
    scoring: { baseScore: 6, firstAttemptBonus: 0, hintPenalty: 1, maxAttempts: 3 },
    achievementTriggers: ["detective-junior-tick", "sprint-02-required-tick"],
    estimateMin: 10,
    skills: { "Requirements Analysis": 3, "Practice Lab": 5, "QA Concepts": 1 },
  },
  {
    id: 'PR-04',
    sprint: 2,
    tier: 'required',
    mode: 'investigate',
    startRoute: '/sandbox/pr-04',
    mountStrategy: 'inline-stub',
    title: "Find missing AC categories в Sign Up Story",
    brief: "Story есть · 3 AC happy path · какие категории missing?",
    taskMd: "Прочитай Story «Sign Up». Просмотри 3 AC. Для каждой category checkbox — отметь, missing она в Story или присутствует. Goal: identify gaps пока Story ещё в требованиях, не в production.",
    hints: [
      { text: "Все 3 AC описывают «всё хорошо · happy path». Что должно случиться, если что-то ПЛОХО?" },
      { text: "Думай: пустой password? email уже занят? `<script>` в username? Это какие категории?" },
      { text: "Negative (validation errors) · Edge (boundary · max length) · Security (XSS/SQLi · injection) — все 3 missing." },
    ],
    scoring: { baseScore: 5, firstAttemptBonus: 0, hintPenalty: 1, maxAttempts: 3 },
    achievementTriggers: ["detective-junior-tick", "sprint-02-required-tick"],
    estimateMin: 12,
    skills: { "Requirements Analysis": 3, "Documentation": 2, "Practice Lab": 5 },
  },
  {
    id: 'PR-05',
    sprint: 2,
    tier: 'required',
    mode: 'author',
    startRoute: '/sandbox/pr-05',
    mountStrategy: 'inline-stub',
    title: "Write 4 AC G/W/T для «Add to cart»",
    brief: "Feature: Add artwork to cart. Напиши 4 AC G/W/T (happy + neg + edge + security).",
    taskMd: "Прочитай brief. Открой Atelier с artwork detail page (для context). Напиши 4 AC в textarea ниже. Используй G/W/T формат. Покрой 4 категории.",
    rubric: {
      items: [
        { id: 'R1', text: "4 AC присутствуют (количество)" },
        { id: 'R2', text: "Все используют Given / When / Then format" },
        { id: 'R3', text: "Покрыты 4 категории: happy + negative + edge + security (по 1 каждой)" },
        { id: 'R4', text: "Конкретные test data в каждой AC (не «valid input» · а реальные значения)" },
        { id: 'R5', text: "Expected result проверяемый (не «всё работает»)" },
        { id: 'R6', text: "Bonus · упомянут negative redirect path / boundary number / specific exploit type" },
      ],
      passThreshold: 4,
    },
    sampleAnswer: "AC-1 [happy]\nGiven user logged in как Аня AND artwork detail page «Lera Sunset 2026» open\nWhen click «Add to cart» button\nThen cart icon counter в шапке increments from 0 to 1 + toast «Lera Sunset 2026 added to cart» appears bottom-right.\n\nAC-2 [negative]\nGiven user NOT logged in (browsing as guest) AND artwork detail page open\nWhen click «Add to cart» button\nThen redirect к /sign-in с return URL `?return=/artwork/lera-2026-014` preserved + after sign-in cart contains item.\n\nAC-3 [edge · cart limit]\nGiven user has 50 items в cart (limit reached) AND artwork detail page open\nWhen click «Add to cart» button\nThen alert dialog «Cart limit 50 items reached. Remove item before adding new.» + cart still 50 items, no addition.\n\nAC-4 [security · API tampering]\nGiven user manipulates POST request body via DevTools to artworkId «xyz999-non-existent»\nWhen server processes request\nThen 404 Not Found returned + no cart mutation + audit log entry с user ID + IP timestamp.",
    scoring: { baseScore: 8, firstAttemptBonus: 0, hintPenalty: 0, maxAttempts: 3 },
    achievementTriggers: ["author-apprentice-tick", "sprint-02-required-tick"],
    estimateMin: 25,
    skills: { "Requirements Analysis": 4, "Documentation": 5, "Practice Lab": 5 },
  },
  {
    id: 'PR-06',
    sprint: 2,
    tier: 'required',
    mode: 'author',
    startRoute: '/sandbox/pr-06',
    mountStrategy: 'inline-stub',
    title: "INVEST evaluation на «Send artwork as gift»",
    brief: "Story дан · оцени 6 букв INVEST с reasoning.",
    taskMd: "Прочитай Story. Для каждой буквы INVEST выбери Pass/Fail + напиши 1 sentence reasoning. Goal — понять, готова ли Story к dev pickup.",
    rubric: {
      items: [
        { id: 'R1', text: "Все 6 букв оценены (I/N/V/E/S/T)" },
        { id: 'R2', text: "Reasoning per letter present (не пустой)" },
        { id: 'R3', text: "I (Independent) · оценено correctly · реализуемо отдельно?" },
        { id: 'R4', text: "E (Estimable) · оценено correctly · scope ясен для estimation?" },
        { id: 'R5', text: "S (Small) · оценено correctly · влезает в спринт?" },
        { id: 'R6', text: "T (Testable) · оценено correctly · QA может verify?" },
      ],
      passThreshold: 4,
    },
    sampleAnswer: "I Independent: Pass — Story self-contained · не depends на другие features в спринте (cart/checkout уже existing).\nN Negotiable: Pass — Open к обсуждению с PM/team как именно реализовать (digital vs physical · tier limitations · etc.).\nV Valuable: Pass — Clear user value · gift-giving = engagement + new user acquisition channel.\nE Estimable: Fail — Слишком vague · «all artwork types» + «digital и physical» — два очень разных flow (digital = email link · physical = shipping). Team не может estimate без scope split.\nS Small: Fail — Multi-feature scope (recipient flow · payment · shipping · digital delivery). Это epic, не story. Должно быть split в 3-4 melter Stories.\nT Testable: Fail — Нет AC. «Easy to use» · «support all types» — non-measurable. Без AC QA не может написать TC.\n\nVerdict: 3 Pass · 3 Fail → Story NOT ready for dev. Recommend: PM split в 3 Stories (digital gift · physical gift · recipient onboarding) + write 4 AC per Story · before grooming pickup.",
    scoring: { baseScore: 6, firstAttemptBonus: 0, hintPenalty: 0, maxAttempts: 3 },
    achievementTriggers: ["author-apprentice-tick", "sprint-02-required-tick"],
    estimateMin: 15,
    skills: { "Requirements Analysis": 4, "Documentation": 2, "Practice Lab": 5 },
  },
  {
    id: 'PR-07',
    sprint: 2,
    tier: 'required',
    mode: 'investigate',
    startRoute: '/sandbox/pr-07',
    mountStrategy: 'inline-stub',
    title: "MoSCoW sort 8 backlog items",
    brief: "8 cards · 4 buckets · drag к правильной priority.",
    taskMd: "Drag каждый из 8 backlog cards в один из 4 buckets: Must / Should / Could / Won't. Goal — все 8 в правильных bucket-ах. Score per item placement.",
    hints: [
      { text: "Must = без этого release не выйдет (sign up · cart · checkout core). Should = enhance · но release выйдет без неё. Could = nice-to-have. Won't = explicitly NOT в этом release." },
      { text: "Что нужно для basic transaction flow (login → buy → pay)? Это Must. Что улучшает UX но не блокирует? Should. Что для нишевых users? Could." },
      { text: "Sign Up = M · Add to cart = M · Place Order = M · Email notif = S · Wishlist = C · Social share = C · Star rating = S · Dark mode = W (deferred)." },
    ],
    scoring: { baseScore: 8, firstAttemptBonus: 0, hintPenalty: 1, maxAttempts: 3 },
    achievementTriggers: ["detective-junior-tick", "sprint-02-required-tick"],
    estimateMin: 12,
    skills: { "Requirements Analysis": 3, "Practice Lab": 5, "QA Concepts": 1 },
  },
  {
    id: 'PR-08',
    sprint: 2,
    tier: 'required',
    mode: 'investigate',
    startRoute: '/sandbox/pr-08',
    mountStrategy: 'inline-stub',
    title: "Find 3 problems в 12-row RTM",
    brief: "RTM 12 rows · 3 проблемы · click problematic rows.",
    taskMd: "Открой RTM table. 12 rows: Req → AC → TC → BR mapping. 3 row имеют traceability issues. Кликни на каждую problematic row. Goal — найти все 3 без false-positives.",
    hints: [
      { text: "Каждая row должна иметь полный chain: Req ID → AC ID → TC ID → BR (если fail). Что-то отсутствует или несовместимо?" },
      { text: "Найди: row где AC висит без TC · row где BR linked но TC Pass (inconsistent) · row где TC без AC reference." },
      { text: "Row 3 (REQ-AUTH-003 / AC-1.3 · empty TC slot) · Row 6 (TC-06 Pass · но BR-02 Major linked) · Row 9 (TC-09 Pass · AC slot empty)." },
    ],
    scoring: { baseScore: 6, firstAttemptBonus: 0, hintPenalty: 1, maxAttempts: 3 },
    achievementTriggers: ["detective-junior-tick", "sprint-02-required-tick"],
    estimateMin: 15,
    skills: { "Requirements Analysis": 3, "Documentation": 2, "Practice Lab": 5 },
  },
  {
    id: 'PR-09',
    sprint: 2,
    tier: 'required',
    mode: 'investigate',
    startRoute: '/sandbox/pr-09',
    mountStrategy: 'inline-stub',
    title: "Static review · pick mode + find 5 issues",
    brief: "Multi-step: pick review form · find 5 issues в requirement.",
    taskMd: "Step 1: прочитай context · select правильную review form. Step 2: после Step 1 success · открывается requirement doc · кликни на каждом из 5 issues (highlights появляются на click). Goal — все 5 без false-positives.",
    hints: [
      { text: "Step 1 · context = junior PM · tight timeline · small team · light formality. Какой mode балансирует quality + speed?" },
      { text: "Step 2 · ищи: vague terms («должны») · missing channels (как notify?) · missing data model (хранение?) · contradictions (free? but paid platform) · missing UX detail (где UI?)." },
      { text: "Step 1 = Walkthrough. Step 2 issues: 1=«должны» (mandatory? optional?) · 2=missing channel · 3=missing data model · 4=contradicts business model · 5=missing UI placement." },
    ],
    scoring: { baseScore: 8, firstAttemptBonus: 0, hintPenalty: 1, maxAttempts: 3 },
    achievementTriggers: ["detective-junior-tick", "sprint-02-required-tick"],
    estimateMin: 18,
    skills: { "Static Testing": 5, "Requirements Analysis": 2, "Practice Lab": 5 },
  },
  {
    id: 'PR-10',
    sprint: 3,
    tier: 'required',
    mode: 'investigate',
    startRoute: '/sandbox/pr-10',
    mountStrategy: 'inline-stub',
    title: "Pick correct test level for 6 scenarios",
    brief: "6 scenarios · 5 test levels · classify each.",
    taskMd: "Прочитай 6 scenarios. Для каждого выбери правильный test level (Component / Comp Integration / System / Sys Integration / Acceptance). 6/6 = full score.",
    hints: [
      { text: "Component = 1 unit/function isolated. Component Integration = 2-3 units together. System = full app E2E. System Integration = с external services. Acceptance = business stakeholder verifies." },
      { text: "«Cart calculation function bug» = isolated logic → Component. «Sign Up + email confirmation flow» = 2 modules → Component Integration. «Full checkout E2E» = System." },
      { text: "1=Component · 2=Comp Integration · 3=System · 4=System Integration · 5=Acceptance · 6=Component (UI utility function)." },
    ],
    scoring: { baseScore: 6, firstAttemptBonus: 0, hintPenalty: 1, maxAttempts: 3 },
    achievementTriggers: ["detective-junior-tick", "sprint-03-required-tick"],
    estimateMin: 12,
    skills: { "Test Types": 3, "Practice Lab": 5, "QA Concepts": 1 },
  },
  {
    id: 'PR-11',
    sprint: 3,
    tier: 'required',
    mode: 'investigate',
    startRoute: '/sandbox/pr-11',
    mountStrategy: 'inline-stub',
    title: "Classify 8 tests by ISO 25010 category",
    brief: "8 tests · F/NF + ISO 25010 category · classify each.",
    taskMd: "Прочитай 8 test descriptions. Для каждого выбери: Functional OR Non-functional + (если NF) → конкретная ISO 25010 category. 8/8 correct = full.",
    hints: [
      { text: "Functional = «что система делает». NF = «как делает» (скорость · safety · UI · multi-browser)." },
      { text: "Performance = speed/load. Security = auth/injection. Usability = UX. Reliability = uptime/error handling. Compatibility = browsers/devices." },
      { text: "1=F · 2=NF Performance · 3=NF Security · 4=NF Usability · 5=F · 6=NF Compatibility · 7=NF Reliability · 8=NF Performance." },
    ],
    scoring: { baseScore: 8, firstAttemptBonus: 0, hintPenalty: 1, maxAttempts: 3 },
    achievementTriggers: ["detective-junior-tick", "sprint-03-required-tick"],
    estimateMin: 15,
    skills: { "Test Types": 3, "Non-functional Testing": 2, "Practice Lab": 5 },
  },
  {
    id: 'PR-12',
    sprint: 3,
    tier: 'required',
    mode: 'investigate',
    startRoute: '/sandbox/pr-12',
    mountStrategy: 'inline-stub',
    title: "Pick Black/White/Gray + extend Black к Gray",
    brief: "5 scenarios · pick approach · затем extend 1 Black test к Gray.",
    taskMd: "Step 1: для каждого из 5 scenarios выбери Black-box / White-box / Gray-box. Step 2: Black scenario №3 «Sign Up form» расширь до Gray-box · что добавишь к UI test (DB check? network check? logs?).",
    hints: [
      { text: "Black = тестируешь только UI/inputs/outputs · код не видишь. White = читаешь код · знаешь internal logic. Gray = UI test + verifying check (DB query · network log · backend log)." },
      { text: "Coverage testing (statement/branch) → White. UAT-style → Black. Smoke test on UI + DB query verify → Gray." },
      { text: "1=Black · 2=White · 3=Black · 4=Gray · 5=White. Step 2 extension: проверить «account row exists в users table» + «password hashed (bcrypt prefix `$2b$`)» + «audit log entry created»." },
    ],
    scoring: { baseScore: 8, firstAttemptBonus: 0, hintPenalty: 1, maxAttempts: 3 },
    achievementTriggers: ["detective-junior-tick", "sprint-03-required-tick"],
    estimateMin: 15,
    skills: { "Test Types": 3, "Practice Lab": 5 },
  },
  {
    id: 'PR-13',
    sprint: 3,
    tier: 'required',
    mode: 'author',
    startRoute: '/sandbox/pr-13',
    mountStrategy: 'inline-stub',
    title: "Change scope + RBT prioritization",
    brief: "Promo bug fix · scope regression + prioritize 6 features.",
    taskMd: "Step 1: identify regression scope (areas affected by promoReducer change). Step 2: prioritize 6 affected features по RBT (Likelihood × Impact matrix · Critical/Important/Moderate/Low). Justify в 1 sentence per feature.",
    rubric: {
      items: [
        { id: 'R1', text: "Step 1 · regression scope identified (cart total · promo apply UI · etc.)" },
        { id: 'R2', text: "Step 2 · all 6 features получают priority (Critical/Important/Moderate/Low)" },
        { id: 'R3', text: "Step 2 · justification в 1 sentence per feature (likelihood + impact)" },
        { id: 'R4', text: "Step 2 · priority distribution roughly соответствует ~60% Critical / 25% Important / 15% Low" },
        { id: 'R5', text: "Step 2 · Critical features get most QA time (justify time allocation)" },
        { id: 'R6', text: "Bonus · упомянут что-то beyond direct scope (related areas: discount-stacking · multi-promo)" },
      ],
      passThreshold: 4,
    },
    sampleAnswer: "Step 1 · Regression scope: affected = (a) cart subtotal/total UI, (b) Place Order final amount, (c) email receipt template, (d) discount-stacking logic (multi-promo), (e) order history past totals (historical data). Untouched = wishlist (no promo path).\n\nStep 2 · Priority allocation (5-day window · 2 QA · ~80h total):\n\nCRITICAL (60% · ~48h): #1 Cart total · #2 Order confirmation · #6 Multi-promo stacking — 16h each · deep + edge cases. High likelihood (direct promo touch) × High impact (money-flow).\nIMPORTANT (25% · ~20h): #3 Email receipt · #4 Order history — 10h each · happy + 2 negatives. Medium likelihood × Medium impact (user trust).\nLOW (15% · ~12h): #5 Wishlist — smoke only · 4h. Low likelihood × Low impact.",
    scoring: { baseScore: 10, firstAttemptBonus: 0, hintPenalty: 0, maxAttempts: 3 },
    achievementTriggers: ["author-apprentice-tick", "sprint-03-required-tick"],
    estimateMin: 25,
    skills: { "Risk-Based Testing": 5, "Test Types": 3, "Documentation": 2, "Practice Lab": 5 },
  },
  {
    id: 'PR-14',
    sprint: 3,
    tier: 'required',
    mode: 'author',
    startRoute: '/sandbox/pr-14',
    mountStrategy: 'inline-stub',
    title: "Write SBTM Test Charter (4-pole format)",
    brief: "60-min charter для «Subscribe to artist» feature.",
    taskMd: "Открой Atelier с artist profile (для context). Напиши Test Charter в 4-pole format: Mission · Heuristic · Areas · Test ideas. Goal — план structured exploration на 60 min.",
    rubric: {
      items: [
        { id: 'R1', text: "Mission · concise · session-scoped (1-2 sentences)" },
        { id: 'R2', text: "Heuristic · named (CRUD / SFDIPOT / FEW-HICCUPPS / «жестокие сценарии») с 1-sentence usage explanation" },
        { id: 'R3', text: "Areas · 3-5 specific zones (subscribe button · digest email · unsubscribe · multi-artist)" },
        { id: 'R4', text: "Test ideas · 3-5 starting points (NOT scripted TCs · open-ended «try X»)" },
        { id: 'R5', text: "Time-box mentioned (60 min · how distribute · ~10m setup + 40m explore + 10m note-taking)" },
        { id: 'R6', text: "Bonus · risk-based prioritization внутри Areas" },
      ],
      passThreshold: 4,
    },
    sampleAnswer: "TEST CHARTER · Subscribe to artist · 60 min · QA: Аня · 2026-05-15\n\nMISSION\nInvestigate новый «Subscribe to artist» flow на edge cases · subscription state · digest email behavior. Goal — найти ≥3 bugs / ≥2 risk areas пока feature в beta.\n\nHEURISTIC\nSFDIPOT (Structure · Function · Data · Interfaces · Platform · Operations · Time). Focus на Function (subscribe action) + Data (subscription persistence) + Time (digest scheduling).\n\nAREAS (5 zones · prioritized)\n1. Subscribe button states (idle / pending / subscribed / unsubscribe) — primary flow\n2. Subscription persistence (login/logout · refresh · multi-device)\n3. Digest email triggers (every 3 days OR upon new artwork? edge: 0 new)\n4. Multi-artist subscriptions (UI list · unsubscribe individual)\n5. Edge cases (subscribe к own profile · subscribe deleted account · cap?)\n\nTEST IDEAS (5 starting points)\n1. Subscribe то же самое artist дважды → counter increment? error? idempotent?\n2. Subscribe → close browser → reopen → subscription state preserved?\n3. Subscribe к 50+ artists → UI handles list? digest size cap?\n4. Subscribed artist deleted account → notification? grace period?\n5. Try subscribe к own artist profile → blocked? allowed?\n\nTIME-BOX\n- 5 min setup (account + 3 sample artists)\n- 45 min exploration (rotate areas · ~9 min each)\n- 10 min note-taking (bugs · areas to revisit · followup)",
    scoring: { baseScore: 8, firstAttemptBonus: 0, hintPenalty: 0, maxAttempts: 3 },
    achievementTriggers: ["author-apprentice-tick", "sprint-03-required-tick"],
    estimateMin: 25,
    skills: { "Exploratory Testing": 5, "Documentation": 3, "Practice Lab": 5 },
  },
  {
    id: 'PR-15',
    sprint: 3,
    tier: 'required',
    mode: 'author',
    startRoute: '/sandbox/pr-15',
    mountStrategy: 'inline-stub',
    title: "Build Compatibility Matrix",
    brief: "Matrix 4-6 features × 4-6 envs · mark each cell.",
    taskMd: "Define 4-6 key Atelier features (rows) × 4-6 environments (columns). Mark each cell: Pass · Known issue (specify) · Critical (specify) · Untested. Goal — actionable matrix к показать PM ещё нужен test pass.",
    rubric: {
      items: [
        { id: 'R1', text: "Matrix имеет 4-6 features (rows) и 4-6 envs (columns)" },
        { id: 'R2', text: "All cells filled (no blanks · ❓ acceptable for untested)" },
        { id: 'R3', text: "Минимум 1 known issue (⚠) или critical (✗) per row · realistic concerns" },
        { id: 'R4', text: "Issues specified конкретно («Safari sticky topbar overlap» / «Samsung dark mode contrast fail»)" },
        { id: 'R5', text: "Environments mix desktop + mobile + tablet (диверсификация)" },
        { id: 'R6', text: "Bonus · столбец/note для a11y compliance level (WCAG A/AA/AAA · EAA legal context)" },
      ],
      passThreshold: 4,
    },
    sampleAnswer: "Feature × Env matrix (Chrome 120 / Safari iOS 17 / Samsung Internet / Edge 119 / Lighthouse Pixel 7):\n\nSign Up flow: Pass / keyboard input lag (~200ms) / Pass / Pass / Pass\nAdd to cart: Pass / Pass / cart icon position drift / Pass / touch target Add btn 38px (<44px WCAG)\nPlace Order checkout: Pass / Apple Pay не triggers (CSP issue) CRITICAL / Pass / form validation styling broken / Pass\nListing browse + filter: Pass / Pass / Untested · BrowserStack queue / filter dropdown overlap / image lazy-load skip on slow 3G\nSubscribe to artist: Pass / Untested · feature beta / Untested / Untested / Untested\n\nRecommendations:\n- P0 fix: Safari Apple Pay — blocks iOS revenue ~30% market.\n- P1 fix: Samsung cart icon · Edge form validation styling · Pixel touch target a11y.\n- P2 followup: BrowserStack run для Subscribe + Samsung Listing browse.\n- A11y note: WCAG AA gap на Pixel touch target — needs fix per EAA legal exposure.",
    scoring: { baseScore: 8, firstAttemptBonus: 0, hintPenalty: 0, maxAttempts: 3 },
    achievementTriggers: ["author-apprentice-tick", "sprint-03-required-tick"],
    estimateMin: 25,
    skills: { "Non-functional Testing": 4, "Documentation": 3, "Practice Lab": 5 },
  },
  {
    id: 'PR-16',
    sprint: 3,
    tier: 'required',
    mode: 'investigate',
    startRoute: '/sandbox/pr-16',
    mountStrategy: 'inline-stub',
    title: "Critique AI-generated test ideas",
    brief: "5 AI ideas · 2 hallucinations · find them + check 6 triggers.",
    taskMd: "Прочитай 5 test ideas от Claude. Кликни на 2 problematic ideas. Объясни в textarea per idea что не так. Затем check 6-trigger чеклист.",
    hints: [
      { text: "Compare each idea с реальным Atelier domain (sign-up = email + password · NO OAuth Google в текущем MVP · NO biometric · NO 2FA в Block 1 scope)." },
      { text: "AI часто invents «standard features» (2FA · OAuth · CAPTCHA) или misapplies pattern (e.g., test «password strength meter» когда такого UI нет)." },
      { text: "Idea #2 (test 2FA · 2FA не существует в Atelier MVP) · Idea #4 (test password strength meter UI · нет такого компонента). 6 triggers · check business-specific · auth · payment · GDPR · third-party · UI components mentioned." },
    ],
    scoring: { baseScore: 6, firstAttemptBonus: 0, hintPenalty: 1, maxAttempts: 3 },
    achievementTriggers: ["detective-junior-tick", "sprint-03-required-tick"],
    estimateMin: 15,
    skills: { "AI Workflow": 5, "Test Types": 2, "Practice Lab": 5 },
  },
  {
    id: 'PR-17',
    sprint: 4,
    tier: 'required',
    mode: 'investigate',
    startRoute: '/sandbox/pr-17',
    mountStrategy: 'inline-stub',
    title: "Classify 8 artifacts по test docs piramida",
    brief: "8 QA artifacts · 6 levels piramid · drag к правильному level.",
    taskMd: "Открой list of 8 QA artifacts (real Atelier examples). Drag каждый artifact на правильный pyramid level. Goal: 8/8 correct + spot 1 redundancy + 1 gap.",
    hints: [
      { text: "Test Strategy = high-level (что/зачем · принципы). Test Plan = sprint/release scope. Scenario = high-level «что тестируем» fрагmент. TC = step-by-step verification. BR = found defect. TSR = post-execution summary." },
      { text: "«Verify cart total с promo applied (steps 1-7)» = TC. «Sign Up flow happy path» (no steps) = Scenario. «Quarterly QA approach: 70% manual, 30% auto» = Strategy." },
      { text: "1=TSR · 2=TC · 3=Test Plan · 4=BR · 5=Scenario · 6=Strategy · 7=TC · 8=Test Plan. Spot: artifact 3 + 8 оба Test Plan (overlap) · нет single TSR для S04 release (gap)." },
    ],
    scoring: { baseScore: 8, firstAttemptBonus: 0, hintPenalty: 1, maxAttempts: 3 },
    achievementTriggers: ["detective-junior-tick", "sprint-04-required-tick"],
    estimateMin: 12,
    skills: { "Test Documentation": 3, "Documentation Practice": 2, "Practice Lab": 5 },
  },
  {
    id: 'PR-18',
    sprint: 4,
    tier: 'required',
    mode: 'author',
    startRoute: '/sandbox/pr-18',
    mountStrategy: 'inline-stub',
    title: "Decompose Place Order Story в 3-4 Test Scenarios",
    brief: "1 Story · 4 AC · напиши 3-4 Test Scenarios.",
    taskMd: "Открой Atelier checkout (для context). Прочитай Story + 4 AC. Напиши 3-4 Test Scenarios в textarea. Каждый Scenario = focused area (e.g., «Happy path Place Order» · «Form validation negative» · etc.).",
    rubric: {
      items: [
        { id: 'R1', text: "3-4 Scenarios написаны (count)" },
        { id: 'R2', text: "Each Scenario имеет название + 1 sentence «что тестируется»" },
        { id: 'R3', text: "Scenarios cover 4 AC distribution (no gap · no major overlap)" },
        { id: 'R4', text: "Scenarios — high-level (NOT step-by-step · НЕ TC level)" },
        { id: 'R5', text: "Bonus · estimated TC count per Scenario (e.g., «~5-7 TCs»)" },
      ],
      passThreshold: 3,
    },
    sampleAnswer: "SCENARIO 1 · Place Order happy path\nVerify successful order creation с valid form data + non-empty cart.\n~5 TCs (single item · multi-item · big total · auto-fill saved card · post-confirm cart cleared).\n\nSCENARIO 2 · Form field validation (negative)\nVerify Place Order form rejects empty или invalid required fields.\n~6 TCs (each of 6 fields empty · invalid CC format · invalid month/year combo · special chars в Name).\n\nSCENARIO 3 · Credit Card boundary + format checks\nVerify CC field handles boundary lengths (1 char · 16 chars · 25 chars) + format edge cases (Visa/MC/Amex prefixes).\n~4 TCs (length boundary trio · prefix validation per card type · expired month/year · future year cap).\n\nSCENARIO 4 · Country × City decision table\nVerify form behavior для 4 combinations: Country empty/filled × City empty/filled.\n~4 TCs (one per matrix cell · expected error or pass per combination).",
    scoring: { baseScore: 6, firstAttemptBonus: 0, hintPenalty: 0, maxAttempts: 3 },
    achievementTriggers: ["author-apprentice-tick", "sprint-04-required-tick"],
    estimateMin: 25,
    skills: { "Test Documentation": 5, "Documentation Practice": 3, "Practice Lab": 5 },
  },
  {
    id: 'PR-19',
    sprint: 4,
    tier: 'required',
    mode: 'author',
    startRoute: '/sandbox/pr-19',
    mountStrategy: 'inline-stub',
    title: "Write IEEE 829 Test Case (atomic) для Sign Up happy",
    brief: "Atelier Sign Up · happy path · напиши TC по IEEE 829 atomic.",
    taskMd: "Открой Atelier Sign Up form (для context). Напиши полный TC в textarea: 9 fields IEEE 829. Atomic style — ONE scenario. Sample available после submit.",
    rubric: {
      items: [
        { id: 'R1', text: "ID present · format `TC-XXX` или similar" },
        { id: 'R2', text: "Title начинается с «Verify» или «Check»" },
        { id: 'R3', text: "Priority (P0/P1/P2/P3) + Type (Positive/Negative/Boundary/Security) указаны" },
        { id: 'R4', text: "Preconditions конкретные (browser · URL · user state)" },
        { id: 'R5', text: "Test Data конкретные (NOT «valid email» · а «test_user_42@mail.ru»)" },
        { id: 'R6', text: "Steps атомарные (1 действие = 1 step) · numbered" },
        { id: 'R7', text: "Expected Result проверяемый (NOT «works» · а observable outcome)" },
      ],
      passThreshold: 5,
    },
    sampleAnswer: "TC ID: TC-AUTH-001\nModule: Sign Up\nTitle: Verify successful Sign Up с valid email и 8-char password\nPriority: P0\nType: Positive\n\nPreconditions:\n- Browser: Chrome 120+ desktop\n- URL: https://atelier.example/sign-up loaded\n- User: not logged in (cleared cookies/localStorage)\n- Email «test_user_42@mail.ru» свободна (если занят — заменить unique)\n\nTest Data:\n- Email: test_user_42@mail.ru\n- Password: Pass1234\n\nSteps:\n1. Открыть https://atelier.example/sign-up в Chrome.\n2. Кликнуть на input «Email» · ввести «test_user_42@mail.ru».\n3. Кликнуть на input «Password» · ввести «Pass1234».\n4. Кликнуть кнопку «Sign up».\n\nExpected Result:\n- Success message «Account created. Confirmation email sent» visible в alert/toast within 2 sec.\n- URL changes к /home (or /onboarding · per spec).\n- Top-right shows username «test_user_42» в avatar dropdown.\n- Confirmation email arrives в inbox within 30 sec (out of TC scope · async check).",
    scoring: { baseScore: 10, firstAttemptBonus: 0, hintPenalty: 0, maxAttempts: 3 },
    achievementTriggers: ["author-apprentice-tick", "sprint-04-required-tick"],
    estimateMin: 35,
    skills: { "Test Documentation": 5, "Documentation Practice": 5, "Practice Lab": 5 },
  },
  {
    id: 'PR-20',
    sprint: 4,
    tier: 'required',
    mode: 'investigate',
    startRoute: '/sandbox/pr-20',
    mountStrategy: 'inline-stub',
    title: "User Story vs Use Case · 5 contexts",
    brief: "5 contexts · pick Story OR UC + 1-sentence reason.",
    taskMd: "Прочитай 5 contexts. Для каждого выбери Story OR UC. Add 1-sentence reasoning. Goal — develop intuition «когда Story · когда UC».",
    hints: [
      { text: "Story = Agile · short · «As a · I want · So that» · negotiable · grooming-friendly. UC = formal · regulated industries · medical/financial/government · 10 fields template." },
      { text: "Startup MVP = Story (fast iteration). Banking compliance = UC (audit trail). Healthcare = UC (FDA traceability). Game indie team = Story." },
      { text: "1=UC · 2=Story · 3=Story · 4=UC · 5=Story (или mixed)." },
    ],
    scoring: { baseScore: 6, firstAttemptBonus: 0, hintPenalty: 1, maxAttempts: 3 },
    achievementTriggers: ["detective-junior-tick", "sprint-04-required-tick"],
    estimateMin: 15,
    skills: { "Requirements Analysis": 3, "Test Documentation": 2, "Practice Lab": 5 },
  },
  {
    id: 'PR-21',
    sprint: 4,
    tier: 'required',
    mode: 'author',
    startRoute: '/sandbox/pr-21',
    mountStrategy: 'inline-stub',
    title: "Write Use Case для «Subscribe to artist» (10 fields)",
    brief: "10-field UC · main flow + 2 alternate flows + exceptions.",
    taskMd: "Открой artist profile (для context). Напиши UC в textarea: 10 fields. Main flow = happy path numbered. Alternate flows = ≥2 «что если...». Exceptions = errors/timeouts.",
    rubric: {
      items: [
        { id: 'R1', text: "Все 10 fields filled (UC ID · Name · Actor · Stakeholders · Pre · Post · Trigger · Main · Alt · Exceptions)" },
        { id: 'R2', text: "Main Flow numbered + 4-7 steps (NOT 1 step · NOT 20 steps)" },
        { id: 'R3', text: "≥2 Alternate Flows · branch logic clear («If X · go to step Y»)" },
        { id: 'R4', text: "≥2 Exceptions named (timeout · invalid state · etc.)" },
        { id: 'R5', text: "Postconditions измеримые («subscription record exists · email sent log entry»)" },
        { id: 'R6', text: "Bonus · трейс к existing User Story OR PRD section" },
      ],
      passThreshold: 4,
    },
    sampleAnswer: "UC ID: UC-SUB-001\nName: Subscribe to artist\nActor: Logged-in Atelier user (subscriber role)\nStakeholders: Subscriber · Artist (Atelier creator) · Atelier system\n\nPreconditions:\n- User logged in\n- Artist profile published (status = «active»)\n- Subscriber not yet subscribed к этому artist\n- Subscriptions feature flag enabled\n\nPostconditions (success):\n- Subscription record created в `subscriptions` table {userId, artistId, ts}\n- Subscriber sees «Unsubscribe» button (state changed)\n- Audit log entry с {action='subscribe', userId, artistId, ts, ip}\n- Welcome email queued для subscriber\n\nTrigger:\nSubscriber clicks «Subscribe» button on artist profile page.\n\nMain Flow:\n1. Subscriber navigates к artist profile page (/artist/<username>).\n2. UI renders «Subscribe» button (state = «idle»).\n3. Subscriber clicks button.\n4. Frontend sends POST /api/subscriptions {artistId}.\n5. Backend validates: user logged · artist active · no existing subscription.\n6. Backend creates subscription record + audit log.\n7. Backend returns 201 Created · subscription object.\n8. UI updates button к «Unsubscribe» (state = «subscribed») · toast «Subscribed!».\n\nAlternate Flows:\nALT-1 · User already subscribed: At step 5, if existing subscription found → return 409 Conflict. At step 8, UI shows toast «Already subscribed» + button stays «Unsubscribe».\nALT-2 · Subscribe к own artist profile: At step 5, if userId == artistId → return 400 Bad Request. At step 8, UI shows tooltip «Cannot subscribe to your own profile».\n\nExceptions:\nEXC-1 · Network timeout (>10 sec): UI shows error «Network slow · retry?» · subscription not created.\nEXC-2 · Backend 500: UI shows error «Something went wrong · please try later» · log to Sentry.",
    scoring: { baseScore: 10, firstAttemptBonus: 0, hintPenalty: 0, maxAttempts: 3 },
    achievementTriggers: ["author-apprentice-tick", "sprint-04-required-tick"],
    estimateMin: 35,
    skills: { "Test Documentation": 5, "Requirements Analysis": 3, "Documentation Practice": 5, "Practice Lab": 5 },
  },
  {
    id: 'PR-22',
    sprint: 4,
    tier: 'required',
    mode: 'author',
    startRoute: '/sandbox/pr-22',
    mountStrategy: 'inline-stub',
    title: "Analyze + rewrite Bug Report (cart loses items)",
    brief: "Bad BR · find ≥3 anti-patterns + rewrite professionally.",
    taskMd: "Step 1: identify ≥3 anti-patterns в данном BR (в textarea). Step 2: rewrite BR в polished template (9-12 fields). Sample available после submit.",
    rubric: {
      items: [
        { id: 'R1', text: "Step 1 · ≥3 anti-patterns identified (vague title · missing repro steps · missing env · etc.)" },
        { id: 'R2', text: "Step 2 · BR ID present (BUG-XXX format)" },
        { id: 'R3', text: "Step 2 · Title atomic + descriptive («Cart loses items after page refresh on Chrome 120 desktop»)" },
        { id: 'R4', text: "Step 2 · Severity AND Priority both set + justified" },
        { id: 'R5', text: "Step 2 · Steps to Reproduce numbered + atomic + reproducible" },
        { id: 'R6', text: "Step 2 · Expected vs Actual contrast clear (specific values · not «works/broken»)" },
        { id: 'R7', text: "Step 2 · Environment specified (browser version · OS · URL · user state)" },
        { id: 'R8', text: "Step 2 · Frequency указан (Always / Often / Sometimes / Rare)" },
      ],
      passThreshold: 6,
    },
    sampleAnswer: "Step 1 · Anti-patterns identified:\n1. Title vague («Cart broken») — нет указания на specific behavior · dev не может grep в логах.\n2. Missing repro steps — junior пишет «sometimes loses» без steps · dev не воспроизведёт.\n3. Missing environment — Chrome какой version? OS? URL? Mobile/desktop?\n4. Severity vs Priority not justified — почему critical/high?\n5. Subjective language («This is bad · needs fix immediately») — devs needs facts.\n6. Missing Expected vs Actual contrast.\n7. Missing Frequency.\n\nStep 2 · Rewrite:\n\nBUG ID: BUG-CART-014\nTitle: Cart loses 2nd+ items after page refresh on Chrome 120 desktop · happy path\nSeverity: Major (data loss · user must re-add · но workaround exists)\nPriority: High (cart = primary purchase flow · affects 100% multi-item users)\n\nEnvironment:\n- Browser: Chrome 120.0.6099 desktop\n- OS: macOS 14.4\n- URL: https://atelier.example/cart\n- User: logged-in («test_user_42@mail.ru»)\n\nPreconditions:\n- Cart contains 2+ items\n- Items added в течение last 5 min (recent session)\n\nSteps to Reproduce:\n1. Open https://atelier.example в Chrome 120.\n2. Sign in as test_user_42.\n3. Open artwork «Lera Sunset» · click «Add to cart» (cart count = 1).\n4. Open artwork «Anya Forest» · click «Add to cart» (cart count = 2).\n5. Click cart icon · открыть /cart page.\n6. Refresh page (F5 OR Ctrl+R).\n\nExpected Result: Cart contains 2 items · cart total persisted.\nActual Result: Cart contains ONLY 1 item («Lera Sunset» · the first added) · 2nd item lost · no error message shown.\nFrequency: Always (reproduced 5/5 attempts)\nWorkaround: re-add lost item.\nSuspected cause: cart state stored in sessionStorage · 2nd add overwrites instead of appending.",
    scoring: { baseScore: 10, firstAttemptBonus: 0, hintPenalty: 0, maxAttempts: 3 },
    achievementTriggers: ["author-apprentice-tick", "sprint-04-required-tick"],
    estimateMin: 30,
    skills: { "Test Documentation": 5, "7a Bug Report Writing": 5, "Documentation Practice": 3, "Practice Lab": 5 },
  },
  {
    id: 'PR-23',
    sprint: 4,
    tier: 'required',
    mode: 'investigate',
    startRoute: '/sandbox/pr-23',
    mountStrategy: 'inline-stub',
    title: "Severity × Priority matrix · 6 bugs",
    brief: "6 bugs · drag к 4×3 S×P matrix · independent axes.",
    taskMd: "Прочитай 6 bug descriptions. Для каждого: pick Severity (Critical/Major/Minor/Trivial) AND Priority (High/Medium/Low). Drag bug card в matrix cell. Goal — все 6 в правильных cells.",
    hints: [
      { text: "Severity = технический impact (data loss · crash · UI). Priority = business urgency (когда fix). Они часто разные!" },
      { text: "Crash на Place Order = Critical Severity + High Priority (money flow). Typo «atelir» вместо «atelier» в hero на homepage = Trivial Severity + High Priority (brand visibility)." },
      { text: "1=Critical/High · 2=Major/High · 3=Minor/Low · 4=Trivial/High (brand) · 5=Major/Medium · 6=Minor/Medium." },
    ],
    scoring: { baseScore: 6, firstAttemptBonus: 0, hintPenalty: 1, maxAttempts: 3 },
    achievementTriggers: ["detective-junior-tick", "sprint-04-required-tick"],
    estimateMin: 15,
    skills: { "Test Documentation": 3, "7a Bug Report Writing": 3, "Practice Lab": 5 },
  },
  {
    id: 'PR-24',
    sprint: 4,
    tier: 'required',
    mode: 'author',
    startRoute: '/sandbox/pr-24',
    mountStrategy: 'inline-stub',
    title: "Write Mini Test Plan для 5-day release",
    brief: "5-day release · 3 features · 2 QA · 6-section Test Plan.",
    taskMd: "Прочитай brief. Напиши Mini Test Plan в textarea: 6 sections (Intro · Scope · Strategy · Env · Schedule · Risks + Entry/Exit). Sample after submit.",
    rubric: {
      items: [
        { id: 'R1', text: "All 6 sections filled (Intro · Scope · Strategy · Env · Schedule · Risks/Entry/Exit)" },
        { id: 'R2', text: "Scope · in/out clear (3 features in · admin panel out · etc.)" },
        { id: 'R3', text: "Strategy · % distribution mentioned (manual/auto/exploratory split)" },
        { id: 'R4', text: "Schedule · day-by-day или milestone-based (NOT generic)" },
        { id: 'R5', text: "Risks · ≥3 named (e.g., «cart promo regression», «Subscribe edge cases», «timeline»)" },
        { id: 'R6', text: "Exit Criteria · MEASURABLE (NOT «всё работает»)" },
        { id: 'R7', text: "Bonus · QA workload distribution per engineer (Аня vs Серж по силам)" },
      ],
      passThreshold: 5,
    },
    sampleAnswer: "MINI TEST PLAN · Atelier v0.5.4 release · 5-day window\nAuthor: Аня (Senior QA) · Date: 2026-05-15\n\n1 · INTRODUCTION\nPurpose: comprehensive test coverage для Atelier v0.5.4 release (3 features) within 5-day Mon-Fri window. 2 QA engineers (Аня + Серж).\n\n2 · SCOPE\nIn scope: Feature A Subscribe to artist (new) · Feature B Sign Up password length validation (fix) · Feature C Cart promo double-discount fix (regression) · Cross-cutting smoke на Sign Up · cart · Place Order.\nOut of scope: Admin panel · Mobile native apps · Performance/load testing · Cross-browser deep scan (Chrome 120 only).\n\n3 · STRATEGY\nManual: 60% (3 days TC + exploratory). Automation: 30% (1.5 days Selenium regression · Аня lead). Exploratory: 10% (0.5 day · Серж · Subscribe edge cases). Test types: Functional · Negative · Boundary · Security.\n\n4 · ENVIRONMENTS\nChrome 120+ desktop · Windows 11 / macOS 14 · staging.atelier.example · 3 pre-created test accounts · seed data 50 artworks · 10 artists · 5 zines.\n\n5 · SCHEDULE\nDay 1 (Mon · Аня): Test Plan finalize + 10 TCs Subscribe + Selenium smoke setup.\nDay 2 (Tue): Аня 10 TCs Sign Up · Серж 8 TCs cart promo.\nDay 3 (Wed): Аня execute Feature A+B · Серж execute C + exploratory Subscribe.\nDay 4 (Thu): Аня full regression suite · Серж re-test fixes.\nDay 5 (Fri): final smoke + Test Summary Report + GO/NO-GO.\n\n6 · RISKS + ENTRY / EXIT\nRisks: R1 Subscribe edge cases · R2 cart regression scope · R3 Серж junior · R4 staging unstable · with mitigations.\nEntry Criteria: features merged · build green · no Critical open · test data seeded.\nExit Criteria: 100% P0 TC pass · ≥85% P1 pass · 0 Sign Up regression · TSR signed · GO/NO-GO submitted.",
    scoring: { baseScore: 12, firstAttemptBonus: 0, hintPenalty: 0, maxAttempts: 3 },
    achievementTriggers: ["author-apprentice-tick", "sprint-04-required-tick"],
    estimateMin: 40,
    skills: { "Test Documentation": 5, "7b Test Plan": 5, "Documentation Practice": 5, "Risk-Based Testing": 2, "Practice Lab": 5 },
  },
  {
    id: 'PR-25',
    sprint: 4,
    tier: 'required',
    mode: 'investigate',
    startRoute: '/sandbox/pr-25',
    mountStrategy: 'inline-stub',
    title: "Critique AI-generated TC + BR · find 3 issues",
    brief: "AI generated 1 TC + 1 BR · 3 issues · find + explain.",
    taskMd: "Прочитай AI-generated TC + BR. Find ≥3 issues (missing field · vague step · wrong S+P · etc.). Annotate each issue в textarea (1 sentence per).",
    hints: [
      { text: "Compare TC fields к IEEE 829 standard (PR-19 sample). Compare BR к 9-12 fields template (PR-22 sample). Check for: missing field · vague text · wrong S/P · non-atomic step · expected/actual gap." },
      { text: "TC may miss «Test Data» specifics OR «Expected Result» OR «Preconditions». BR may miss «Frequency» OR «Environment» OR have wrong S/P combo." },
      { text: "TC issue: «Steps» сводятся к 1 line («Sign up with valid data»). BR issue: Severity=High Priority=High пара одинаковая · нет justification. BR issue 2: missing reproduction Steps numbered." },
    ],
    scoring: { baseScore: 8, firstAttemptBonus: 0, hintPenalty: 1, maxAttempts: 3 },
    achievementTriggers: ["detective-junior-tick", "sprint-04-required-tick"],
    estimateMin: 15,
    skills: { "AI Workflow": 5, "Test Documentation": 3, "Practice Lab": 5 },
  },
  {
    id: 'PR-26',
    sprint: 5,
    tier: 'required',
    mode: 'investigate',
    startRoute: '/sandbox/pr-26',
    mountStrategy: 'inline-stub',
    title: "Pick correct test technique для 6 scenarios",
    brief: "6 features · pick EP/BVA/DT/ST/Pairwise/UCT для каждого.",
    taskMd: "Прочитай 6 feature descriptions. Для каждого pick technique наиболее подходящую. Goal — develop intuition «когда какая technique».",
    hints: [
      { text: "EP = input ranges (length · numbers · categories). BVA = boundary values конкретно (min-1 · min · min+1). DT = combinations rules (если X и Y → Z). ST = states + transitions (lifecycle). Pairwise = много factors много values · сократить combinations. UCT = main + alternate flow coverage." },
      { text: "«Username 1-200 chars» = EP+BVA pair. «Approve order: amount × user-tier × promo» = DT. «Order status flow: pending → paid → shipped» = ST. «5 browsers × 4 OS × 3 resolutions» = Pairwise (60 combos → ~12)." },
      { text: "1=EP+BVA · 2=DT · 3=ST · 4=Pairwise · 5=UCT · 6=BVA (timezone boundary)." },
    ],
    scoring: { baseScore: 6, firstAttemptBonus: 0, hintPenalty: 1, maxAttempts: 3 },
    achievementTriggers: ["detective-junior-tick", "sprint-05-required-tick"],
    estimateMin: 12,
    skills: { "Test Design": 3, "Test Types": 1, "Practice Lab": 5 },
  },
  {
    id: 'PR-27',
    sprint: 5,
    tier: 'required',
    mode: 'author',
    startRoute: '/sandbox/pr-27',
    mountStrategy: 'inline-stub',
    title: "EP + BVA combined для promo code (6-12 chars)",
    brief: "Promo code 6-12 char alphanumeric · EP classes + BVA boundaries + 8-10 TCs.",
    taskMd: "Открой Atelier checkout (для context). Step 1: identify EP classes (valid · invalid types). Step 2: BVA boundary values (5 · 6 · 7 · 11 · 12 · 13). Step 3: write 8-10 TCs combining both. Sample after submit.",
    rubric: {
      items: [
        { id: 'R1', text: "EP classes identified (valid range · empty · too short · too long · invalid chars · special)" },
        { id: 'R2', text: "BVA boundary values listed (5 · 6 · 7 · 11 · 12 · 13 char lengths)" },
        { id: 'R3', text: "8-10 TCs written (count)" },
        { id: 'R4', text: "Each TC has Type indicator (Positive/Negative/Boundary)" },
        { id: 'R5', text: "TC Test Data конкретные (e.g., «STUDIO» 6 chars · «STUDI» 5 chars · NOT generic)" },
        { id: 'R6', text: "Coverage: ≥1 happy + ≥3 boundary + ≥2 negative chars" },
        { id: 'R7', text: "Bonus · invalid char categories named (lowercase · special chars · spaces · numbers leading)" },
      ],
      passThreshold: 5,
    },
    sampleAnswer: "Step 1 · EP classes:\n- Valid: length 6-12 chars · A-Z + 0-9 only (e.g., STUDIO · GIFT2026 · ARTIST)\n- Invalid · too short: length < 6 (1-5 chars)\n- Invalid · too long: length > 12 (13+ chars)\n- Invalid · empty: 0 chars\n- Invalid · charset: contains lowercase · special chars · spaces\n\nStep 2 · BVA boundary values: 5 · 6 · 7 · 11 · 12 · 13 (focus around min=6 and max=12)\n\nStep 3 · 8 TCs:\nTC-PROMO-01 Positive «STUDIO» (6) → Promo applied · -10% discount visible\nTC-PROMO-02 Boundary «GIFT2026PROM» (12) → Promo applied OR error «not found»\nTC-PROMO-03 Boundary «STUDI» (5) → Red border + tooltip «Invalid format»\nTC-PROMO-04 Boundary «GIFT2026PROMO» (13) → Red border + tooltip\nTC-PROMO-05 Negative «» (0) → Apply button disabled · OR red border\nTC-PROMO-06 Positive «studio» → «STUDIO» (case-insensitive) → Promo applied\nTC-PROMO-07 Negative «STUDIO!» → Red border\nTC-PROMO-08 Negative «STUDIO 10» (space) → Red border",
    scoring: { baseScore: 10, firstAttemptBonus: 0, hintPenalty: 0, maxAttempts: 3 },
    achievementTriggers: ["author-apprentice-tick", "sprint-05-required-tick"],
    estimateMin: 30,
    skills: { "Test Design": 5, "Test Documentation": 3, "Practice Lab": 5 },
  },
  {
    id: 'PR-28',
    sprint: 5,
    tier: 'required',
    mode: 'author',
    startRoute: '/sandbox/pr-28',
    mountStrategy: 'inline-stub',
    title: "Decision Table для subscription discount logic",
    brief: "3 binary conditions · 8 rules · build DT + minimize + 4-6 TCs.",
    taskMd: "Step 1: list 3 conditions + 1 outcome var. Step 2: build full DT (8 rows) с outcomes per rule. Step 3: minimize (collapse duplicates). Step 4: write 4-6 TCs covering unique outcomes.",
    rubric: {
      items: [
        { id: 'R1', text: "Step 1 · conditions identified (3 binary)" },
        { id: 'R2', text: "Step 2 · DT 8 rules listed (2³ all combinations)" },
        { id: 'R3', text: "Step 2 · outcome per rule specified (% discount)" },
        { id: 'R4', text: "Step 3 · minimization attempted (duplicates merged via «-» don't care)" },
        { id: 'R5', text: "Step 4 · 4-6 TCs written · each covering unique outcome" },
        { id: 'R6', text: "Bonus · реалистичные realistic пример promo codes (STUDIO10 · NEWBIE · etc.)" },
      ],
      passThreshold: 4,
    },
    sampleAnswer: "Step 1 · Conditions:\n- C1: Promo code valid (Y/N)\n- C2: Premium tier (Creator+ или Creator) (Y/N)\n- C3: First-purchase user (Y/N)\n- Outcome: Discount % (0 · 10 · 15 · 20 · 25)\n\nStep 2 · Decision Table (8 rules):\nR1 Y/Y/Y → 25% (max stack)\nR2 Y/Y/N → 20% (promo + tier)\nR3 Y/N/Y → 20% (promo + first)\nR4 Y/N/N → 10% (promo only)\nR5 N/Y/Y → 15% (tier + first)\nR6 N/Y/N → 10% (tier only)\nR7 N/N/Y → 10% (first-purchase only)\nR8 N/N/N → 0% (no discount)\n\nStep 3 · Minimization: R4 + R6 + R7 same 10% outcome (different conditions but same outcome · acceptable separate). 25% is max cap.\n\nStep 4 · 6 TCs (covering unique outcomes 0/10/15/20/25):\nTC-DISC-01 R8 Free user · no promo · returning → full price\nTC-DISC-02 R4 Free user · STUDIO10 · returning → -10%\nTC-DISC-03 R6 Creator+ user · no promo · returning → -10%\nTC-DISC-04 R5 Creator+ user · no promo · first time → -15%\nTC-DISC-05 R2 Creator+ user · STUDIO10 · returning → -20%\nTC-DISC-06 R1 Creator+ user · STUDIO10 · first time → -25% (max cap)",
    scoring: { baseScore: 10, firstAttemptBonus: 0, hintPenalty: 0, maxAttempts: 3 },
    achievementTriggers: ["author-apprentice-tick", "sprint-05-required-tick"],
    estimateMin: 35,
    skills: { "Test Design": 5, "Test Documentation": 3, "Practice Lab": 5 },
  },
  {
    id: 'PR-29',
    sprint: 5,
    tier: 'required',
    mode: 'author',
    startRoute: '/sandbox/pr-29',
    mountStrategy: 'inline-stub',
    title: "State Transition · account lifecycle с email confirm",
    brief: "5 states · 8+ transitions · diagram + 6-8 TCs (0-switch · 1-switch · 2-switch · invalid).",
    taskMd: "Step 1: list 5 states + valid transitions (e.g., Pending Confirm → Active via «click link in 24h»). Step 2: write 6-8 TCs by ISTQB coverage levels: 0-switch · 1-switch · 2-switch · invalid transitions.",
    rubric: {
      items: [
        { id: 'R1', text: "Step 1 · 5 states listed" },
        { id: 'R2', text: "Step 1 · valid transitions enumerated (≥7) с trigger event each" },
        { id: 'R3', text: "Step 2 · 6-8 TCs written" },
        { id: 'R4', text: "Step 2 · ≥1 TC за coverage level (0-switch · 1-switch · 2-switch)" },
        { id: 'R5', text: "Step 2 · ≥1 invalid transition test (е.g., Banned → Active should be impossible)" },
        { id: 'R6', text: "Bonus · timing constraint tested (24h link expiration)" },
      ],
      passThreshold: 4,
    },
    sampleAnswer: "Step 1 · States + Transitions:\nStates: Pending Confirm · Active · Inactive · Disabled · Banned\nTransitions:\n1. (Sign Up) → Pending Confirm [trigger: form submit]\n2. Pending Confirm → Active [trigger: email link click within 24h]\n3. Pending Confirm → Inactive [trigger: 24h timer expires without click]\n4. Inactive → Pending Confirm [trigger: user requests re-send link]\n5. Active → Disabled [trigger: user clicks «Deactivate account»]\n6. Disabled → Active [trigger: user clicks «Reactivate»]\n7. Active → Banned [trigger: admin action]\n8. Disabled → Banned [trigger: admin action]\nBanned = terminal (no outbound transitions).\n\nStep 2 · 7 TCs by coverage:\nTC-ST-01 0-switch · Verify Pending Confirm state · UI shows «check email» banner · login disabled\nTC-ST-02 1-switch · Verify Pending Confirm → Active when link clicked в 24h · login enabled · welcome email\nTC-ST-03 1-switch (timing) · Verify Pending Confirm → Inactive when 25h passed · re-send button visible · link expired msg\nTC-ST-04 2-switch · Verify Inactive → Pending Confirm → Active flow (re-send · click) · account active after 2 transitions\nTC-ST-05 1-switch · Verify Active → Disabled via «Deactivate» button + confirm · login blocked\nTC-ST-06 invalid · Verify Banned account cannot revert via Reactivate API call · 403 Forbidden · «contact support»\nTC-ST-07 invalid · Verify Disabled cannot directly skip к Active без user click · 401 · «session inactive»",
    scoring: { baseScore: 10, firstAttemptBonus: 0, hintPenalty: 0, maxAttempts: 3 },
    achievementTriggers: ["author-apprentice-tick", "sprint-05-required-tick"],
    estimateMin: 35,
    skills: { "Test Design": 5, "Test Documentation": 3, "Practice Lab": 5 },
  },
  {
    id: 'PR-30',
    sprint: 5,
    tier: 'required',
    mode: 'author',
    startRoute: '/sandbox/pr-30',
    mountStrategy: 'inline-stub',
    title: "Build Pairwise table для login (4 factors × 3 values)",
    brief: "4 factors · 3 values each · pairwise table reduces 64 → ~12-15 TCs.",
    taskMd: "Step 1: list 4 factors + 3 values each. Step 2: build pairwise table (~12-15 rows · all pairs covered). Step 3: explain ROI (64 → 15 = 4× reduction · still 100% pair coverage).",
    rubric: {
      items: [
        { id: 'R1', text: "Step 1 · 4 factors × 3 values listed" },
        { id: 'R2', text: "Step 2 · pairwise table 12-18 rows (acceptable range · NOT 64 full table)" },
        { id: 'R3', text: "Step 2 · table format clear (rows = TCs · cols = factors)" },
        { id: 'R4', text: "Step 2 · spot-check 1 pair: e.g., Chrome+Wifi appears ≥1 row · Safari+OAuth appears ≥1 row" },
        { id: 'R5', text: "Bonus · explain ROI calculation OR mention tool (PICT/Hexawise)" },
      ],
      passThreshold: 3,
    },
    sampleAnswer: "Step 1 · Factors:\n- F1 Browser: Chrome · Safari · Firefox\n- F2 OS: Win · Mac · Linux\n- F3 Connection: WiFi · 4G · 3G\n- F4 Auth: Password · OAuth · MagicLink\n\nStep 2 · Pairwise table (~12 rows · PICT-style):\n1 Chrome / Win / WiFi / Password\n2 Chrome / Mac / 4G / OAuth\n3 Chrome / Linux / 3G / MagicLink\n4 Safari / Win / 4G / MagicLink\n5 Safari / Mac / WiFi / OAuth\n6 Safari / Linux / 3G / Password\n7 Firefox / Win / 3G / OAuth\n8 Firefox / Mac / WiFi / MagicLink\n9 Firefox / Linux / 4G / Password\n10 Chrome / Win / 3G / OAuth\n11 Safari / Mac / 4G / Password\n12 Firefox / Linux / WiFi / OAuth\n\nSpot-checks: Chrome+Mac row 2 · Safari+OAuth row 5 · 4G+MagicLink row 4 · Firefox+Win row 7.\n\nStep 3 · ROI:\n- Full combinatorial: 3⁴ = 81 TCs\n- Pairwise: 12 TCs = ~6.5× reduction · still 100% pair coverage (catches ~80% defects per industry studies)\n- Tools: PICT (free Microsoft) · Hexawise (commercial) · ACTS (NIST)",
    scoring: { baseScore: 8, firstAttemptBonus: 0, hintPenalty: 0, maxAttempts: 3 },
    achievementTriggers: ["author-apprentice-tick", "sprint-05-required-tick"],
    estimateMin: 30,
    skills: { "Test Design": 5, "Test Documentation": 3, "Practice Lab": 5 },
  },
  {
    id: 'PR-31',
    sprint: 5,
    tier: 'required',
    mode: 'author',
    startRoute: '/sandbox/pr-31',
    mountStrategy: 'inline-stub',
    title: "Use Case Testing для UC-SUB-001 (cross-sprint chain S04→S05)",
    brief: "UC из S04 · derive 6-8 TCs от main + alt flows + exceptions.",
    taskMd: "Step 1: pull UC structure (main flow + 2 alternate + 2 exceptions = 5 paths). Step 2: write 6-8 TCs covering each path. Step 3: spot overlap с BVA/DT (e.g., timeout boundary = BVA · ALT × EXC = DT).",
    rubric: {
      items: [
        { id: 'R1', text: "Step 1 · 5 UC paths identified (main + ALT-1 + ALT-2 + EXC-1 + EXC-2)" },
        { id: 'R2', text: "Step 2 · 6-8 TCs written · ≥1 per UC path covered" },
        { id: 'R3', text: "Step 2 · TC titles map к path («Verify main · happy» · «Verify ALT-1 · already subscribed»)" },
        { id: 'R4', text: "Step 2 · TC Test Data realistic (specific user IDs · artist usernames · etc.)" },
        { id: 'R5', text: "Step 3 · ≥1 cross-technique overlap noted (e.g., timeout boundary = also BVA · ALT × EXC = DT)" },
        { id: 'R6', text: "Bonus · trace back к UC fields (Preconditions · Postconditions · etc.)" },
      ],
      passThreshold: 4,
    },
    sampleAnswer: "Step 1 · 5 UC paths:\n- Main flow: 8 steps (Sign in → click Subscribe → POST → DB record → response → UI update)\n- ALT-1: User already subscribed (409 Conflict)\n- ALT-2: Subscribe own profile (400 Bad Request)\n- EXC-1: Network timeout (>10s)\n- EXC-2: Backend 500 error\n\nStep 2 · 7 TCs covering 5 paths:\nTC-UCT-01 Main flow happy · user=test_42 · artist=lera · not subscribed → 201 · button «Unsubscribe» · «Subscribed!» toast · audit log\nTC-UCT-02 Main flow welcome email · user=test_42 · artist=lera → Welcome email queued · receives within 30s\nTC-UCT-03 ALT-1 already subscribed · user=test_42 · artist=lera (already subscribed) → 409 · toast «Already subscribed» · button stays «Unsubscribe»\nTC-UCT-04 ALT-2 own profile · user=lera · artist=lera → 400 · tooltip «Cannot subscribe to own profile» · NO API call\nTC-UCT-05 EXC-1 timeout · user=test_42 · artist=lera · network slow >10s → Error «Network slow · retry?» · NO subscription\nTC-UCT-06 EXC-2 500 · user=test_42 · artist=lera · backend mock 500 → Error «Something went wrong» · Sentry log · NO DB mutation\nTC-UCT-07 Main flow button state · user=test_42 · artist=lera → idle → pending → subscribed (3 states visible)\n\nStep 3 · Cross-technique overlap:\n- TC-UCT-05 (timeout 10s) = also BVA (boundary value: 9s pass · 10s edge · 11s fail)\n- ALT-1 + ALT-2 + EXC-1 + EXC-2 = expressible как Decision Table (4 condition combos · 4 outcomes)\n- TC-UCT-07 (button states) = also State Transition (idle → pending → subscribed = 2-switch)",
    scoring: { baseScore: 10, firstAttemptBonus: 0, hintPenalty: 0, maxAttempts: 3 },
    achievementTriggers: ["author-apprentice-tick", "sprint-05-required-tick"],
    estimateMin: 30,
    skills: { "Test Design": 5, "Test Documentation": 5, "Requirements Analysis": 2, "Practice Lab": 5 },
  },
  {
    id: 'PR-32',
    sprint: 5,
    tier: 'required',
    mode: 'author',
    startRoute: '/sandbox/pr-32',
    mountStrategy: 'inline-stub',
    title: "Metrics dashboard + GO/NO-GO recommendation",
    brief: "Raw QA data · compute 6 metrics + write GO/NO-GO в 2-3 sentences.",
    taskMd: "Step 1: compute 6 key metrics from raw data (Pass rate · Defect density · Test execution rate · Critical bug count · Schedule variance · Coverage). Step 2: write GO/NO-GO recommendation 2-3 sentences с обоснованием.",
    rubric: {
      items: [
        { id: 'R1', text: "All 6 metrics computed (formulas correct)" },
        { id: 'R2', text: "Pass rate = passed / executed × 100% (ratio of TCs)" },
        { id: 'R3', text: "Defect density = bugs / TC × 100 (per Slot H S05 T08)" },
        { id: 'R4', text: "GO/NO-GO recommendation 2-3 sentences" },
        { id: 'R5', text: "Recommendation justified by ≥2 metrics («1 Critical open + 81% pass rate · NO-GO ...»)" },
        { id: 'R6', text: "Conditional GO mentioned (e.g., «GO if Critical fixed by Tue · else NO-GO»)" },
        { id: 'R7', text: "Bonus · trend/vs target context (e.g., «80% target met for pass rate · но Critical opens skewed»)" },
      ],
      passThreshold: 5,
    },
    sampleAnswer: "Step 1 · 6 Key Metrics:\n1. Pass Rate = passed / executed × 100% = 38/47 = 80.9% (target ≥80% · met)\n2. Defect Density = bugs / TC × 100 = 9/47 × 100 = 19% (target <30% · met)\n3. Test Execution Rate = executed / planned × 100% = 47/50 = 94% (target ≥90% · met)\n4. Critical Bug Count = count(Critical · open) = 1 open (target 0 · FAIL)\n5. Schedule Variance = (actual - planned) / planned = 0.5/5 = +10% (target ±15% · met)\n6. Coverage P0 = P0 features tested / P0 features total = 9/9 = 100% (met)\n\nStep 2 · GO/NO-GO Recommendation:\nNO-GO · условный. 1 Critical bug open (BUG-CART-014: cart loses items after refresh) blocks ship. Все остальные metrics соответствуют targets (Pass rate 81% · Coverage 100% P0 · CTFL gate met). Recommendation: дополнительный 1-day delay для Critical fix · re-test · then conditional GO. Если Critical fix ready by Tuesday EOD · ship Wednesday. Если нет · skip к v0.5.5 (next 2-week cycle).",
    scoring: { baseScore: 10, firstAttemptBonus: 0, hintPenalty: 0, maxAttempts: 3 },
    achievementTriggers: ["author-apprentice-tick", "sprint-05-required-tick"],
    estimateMin: 30,
    skills: { "Test Documentation": 5, "7b Test Plan": 3, "Risk-Based Testing": 3, "Practice Lab": 5 },
  },
  {
    id: 'PR-33',
    sprint: 5,
    tier: 'required',
    mode: 'investigate',
    startRoute: '/sandbox/pr-33',
    mountStrategy: 'inline-stub',
    title: "Critique AI-generated EP+BVA test design · find 3 issues",
    brief: "AI table · 3 issues · find + explain.",
    taskMd: "Прочитай AI-generated 8-TC table. Find ≥3 issues (wrong boundary value · missing EP class · technique confusion). Annotate each в textarea.",
    hints: [
      { text: "Compare AI output к canonical EP+BVA structure (per PR-27 sample). Check: (a) BVA values include min-1/min/min+1 AND max-1/max/max+1? (b) EP classes cover invalid types AND empty? (c) Type column matches technique?" },
      { text: "Common AI issues: forgets «too young» (under 18) · uses BVA values (17 · 18 · 19) but skips upper boundary (64 · 65 · 66) · mislabels Boundary tests as Positive." },
      { text: "Issue 1: missing «too young» EP class (no test для age=10 invalid · only age <18 boundaries). Issue 2: upper boundary BVA incomplete (only 65 · 66 · нет 64 · 65 · 66 trio). Issue 3: TC #5 labeled Positive хотя должно быть Boundary (testing 18 = min boundary)." },
    ],
    scoring: { baseScore: 6, firstAttemptBonus: 0, hintPenalty: 1, maxAttempts: 3 },
    achievementTriggers: ["detective-junior-tick", "sprint-05-required-tick"],
    estimateMin: 15,
    skills: { "AI Workflow": 5, "Test Design": 3, "Practice Lab": 5 },
  },
];

export const SCENARIOS: ReadonlyArray<Scenario> = [
  ...BLOCK_1_SCENARIOS,
  ...BLOCK_1_FOUND_SCENARIOS,
  ...BLOCK_2_SCENARIOS,
  ...BLOCK_3_SCENARIOS,
  ...BLOCK_4_SCENARIOS,
];

export const SCENARIOS_BY_ID: Record<string, Scenario> = Object.fromEntries(
  SCENARIOS.map((s) => [s.id, s]),
);

export function scenarioById(id: string): Scenario | undefined {
  return SCENARIOS_BY_ID[id];
}

export function scenariosBySprint(sprintNum: number): Scenario[] {
  return SCENARIOS.filter((s) => s.sprint === sprintNum);
}

export function bonusScenariosBySprint(sprintNum: number): Scenario[] {
  return SCENARIOS.filter((s) => s.sprint === sprintNum && s.tier === 'bonus');
}